import { MS_DAY, diffDays, toTime } from "@components/Timeline/timelineDateUtils";
import { dateToX } from "@components/Timeline/employeeTimelineGeometry";
import { normalizePeriodBarState } from "@components/PeriodBar/periodBarUtils";
import { formatPayRangeShort } from "./employeeTimelinePayRange";

/** User-facing lifecycle labels (draft → Proposed per ER-12036 / PO 2026-10-04). */
export const PERIOD_STATUS_LABEL = {
  draft: "Proposed",
  current: "Current",
  future: "Future",
  lapsed: "Lapsed",
};

const MIN_GAP_DAYS = 1;
const MIN_GAP_PX = 20;

export function normalizeRecord(raw) {
  return {
    id: raw.id ?? raw.key ?? `${raw.label}-${raw.effectiveFrom}`,
    label: String(raw.label ?? raw.name ?? ""),
    state: normalizePeriodBarState(raw.state ?? raw.status),
    effectiveFrom: raw.effectiveFrom ?? raw.startDate ?? null,
    effectiveTo: raw.effectiveTo ?? raw.endDate ?? null,
    workflowState: raw.workflowState ?? raw.stateLabel ?? raw.subState ?? "",
    selectable: raw.selectable !== false,
    meta: raw.meta,
    minAmount: raw.minAmount ?? null,
    maxAmount: raw.maxAmount ?? null,
    currency: raw.currency ?? null,
    payBasis: raw.payBasis ?? null,
  };
}

export function displayLabel(record) {
  return String(normalizeRecord(record).label || "").trim();
}

export function displayDetail(record) {
  return formatPayRangeShort(normalizeRecord(record));
}

function recordIntervalMs(record, windowEndMs) {
  const start = toTime(record.effectiveFrom) ?? 0;
  let end = toTime(record.effectiveTo);
  if (end == null) end = windowEndMs;
  return { start, end };
}

function overlaps(a, b) {
  return a.start < b.end && b.start < a.end;
}

/** Pack records into rows; overlapping drafts stack beneath. */
export function layoutRecordRows(records, windowStartMs, windowEndMs, trackWidthPx, todayMs) {
  const normalized = records.map(normalizeRecord);
  const rows = [];

  normalized.forEach((record) => {
    const interval = recordIntervalMs(record, windowEndMs);
    let placed = false;
    for (let r = 0; r < rows.length; r += 1) {
      const rowIntervals = rows[r].map((item) => item.interval);
      if (!rowIntervals.some((existing) => overlaps(existing, interval))) {
        rows[r].push({ record, interval });
        placed = true;
        break;
      }
    }
    if (!placed) {
      rows.push([{ record, interval }]);
    }
  });

  return rows.map((row, rowIndex) =>
    layoutRowSegments(row, windowStartMs, windowEndMs, trackWidthPx, todayMs, rowIndex)
  );
}

function layoutRowSegments(rowItems, windowStartMs, windowEndMs, trackWidthPx, todayMs, rowIndex) {
  const sorted = [...rowItems].sort((a, b) => a.interval.start - b.interval.start);
  const segments = [];
  let cursor = windowStartMs;

  sorted.forEach(({ record, interval }) => {
    const barStart = Math.max(interval.start, windowStartMs);
    const barEnd = Math.min(interval.end, windowEndMs);
    if (barEnd <= windowStartMs || barStart >= windowEndMs) return;

    const gapStart = Math.max(cursor, windowStartMs);
    const gapEnd = barStart;
    if (gapEnd > gapStart + MIN_GAP_DAYS * MS_DAY) {
      const gapDays = diffDays(gapStart, gapEnd);
      const leftPx = dateToX(gapStart, windowStartMs, windowEndMs, trackWidthPx);
      const widthPx = dateToX(gapEnd, windowStartMs, windowEndMs, trackWidthPx) - leftPx;
      if (gapDays >= MIN_GAP_DAYS && widthPx >= MIN_GAP_PX) {
        segments.push({
          type: "gap",
          key: `gap-${rowIndex}-${gapStart}`,
          leftPx,
          widthPx,
          label: `${gapDays}d`,
        });
      }
    }

    const openEnded = record.effectiveTo == null || record.effectiveTo === "";
    const leftPx = dateToX(barStart, windowStartMs, windowEndMs, trackWidthPx);
    let rightPx = openEnded
      ? trackWidthPx
      : dateToX(barEnd, windowStartMs, windowEndMs, trackWidthPx);
    rightPx = Math.min(trackWidthPx, Math.max(leftPx, rightPx));
    const widthPx = Math.max(0, rightPx - leftPx);

    if (widthPx > 0) {
      segments.push({
        type: "bar",
        key: `bar-${record.id}-${rowIndex}`,
        record,
        leftPx,
        widthPx,
        openEnded,
        label: displayLabel(record),
        detail: displayDetail(record),
      });
    }

    cursor = Math.max(cursor, openEnded ? windowEndMs : barEnd);
  });

  return segments;
}

export function collectVisibleStates(recordSets) {
  const states = new Set();
  recordSets.flat().forEach((r) => {
    states.add(normalizePeriodBarState(r?.state ?? r?.status));
  });
  return states;
}
